import { errorCode } from '../services/api'

/**
 * The profile photo on the client (V5-w, `me_uploadPhoto` / `me_removePhoto`).
 *
 * The server re-encodes every upload to a 512×512 JPEG without metadata, so
 * all the client does is keep the upload small: crop the middle square,
 * draw it at 512×512 on a canvas, send that as a JPEG. The canvas also drops
 * whatever EXIF the original carried before a byte leaves the device.
 */

/** The server's output size; there is no point sending more. */
export const PHOTO_SIZE = 512

/** JPEG quality of the shrunk copy — the server re-encodes it anyway. */
export const PHOTO_QUALITY = 0.9

/** The centred square of a `width`×`height` picture, as canvas source coordinates. */
export function squareCrop(width, height) {
  const w = Math.max(0, Math.floor(Number(width) || 0))
  const h = Math.max(0, Math.floor(Number(height) || 0))
  const size = Math.min(w, h)
  return { sx: Math.floor((w - size) / 2), sy: Math.floor((h - size) / 2), size }
}

/**
 * What the server's answer to an upload means for the page:
 * - `disabled` — 403 `photos_disabled`: photos were switched off after the
 *   profile was loaded (`social.photosEnabled`), so the control goes away
 *   with a neutral line;
 * - `rejected` — 422 `photo_rejected`: not a picture it could read, or too big;
 * - `offline` — the request never reached the server;
 * - `service` — anything else.
 */
export function photoFailure(error) {
  if (!error?.response) return 'offline'
  const code = errorCode(error)
  if (code === 'photos_disabled') return 'disabled'
  if (code === 'photo_rejected') return 'rejected'
  return 'service'
}

/**
 * The picture a learner chose, shrunk to a `PHOTO_SIZE` square JPEG Blob.
 * Browser-only (canvas); rejects when the file is not an image the browser
 * can decode.
 */
export async function shrinkPhoto(file) {
  const image = await decodeImage(file)
  try {
    const { sx, sy, size } = squareCrop(image.width, image.height)
    if (size === 0) throw new Error('empty image')
    const canvas = document.createElement('canvas')
    canvas.width = PHOTO_SIZE
    canvas.height = PHOTO_SIZE
    const context = canvas.getContext('2d')
    // White under a transparent PNG, as the server flattens it.
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, PHOTO_SIZE, PHOTO_SIZE)
    context.drawImage(image.source, sx, sy, size, size, 0, 0, PHOTO_SIZE, PHOTO_SIZE)
    return await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), 'image/jpeg', PHOTO_QUALITY)
    })
  } finally {
    image.release()
  }
}

async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') {
    // `from-image` applies the EXIF orientation, so a phone photo is not drawn on its side.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close?.() }
  }
  const url = URL.createObjectURL(file)
  try {
    const element = await new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('not an image'))
      img.src = url
    })
    return { source: element, width: element.naturalWidth, height: element.naturalHeight, release: () => URL.revokeObjectURL(url) }
  } catch (error) {
    URL.revokeObjectURL(url)
    throw error
  }
}
